import { storeToRefs } from 'pinia';
import { computed, nextTick, onMounted, Ref, ref, watch } from 'vue';

import { useViewerItemStore } from '../store/useViewerItemStore';
import { useUser } from './useUser';
import { useTranscriptionStore, ItemTranscriptionStatus } from '../store/useTranscriptionStore';

import { ItemType } from '../store/useViewerItemStore';

const DEFAULT_TABS_ID = '#layout-tabs';
const SCROLL_OFFSET = 250;

export enum SubheaderTabs {
  DISCUSSION = '#discussion',
  TRANSCRIPTIONS = '#transcriptions',
  ANALYTICS = '#analytics',
}

const selectedTab: Ref<SubheaderTabs> = ref(SubheaderTabs.DISCUSSION);
const availableTabs: Ref<SubheaderTabs[]> = ref([]);

export const useSubheaderTabs = () => {
  const store = useViewerItemStore();
  const transcriptionStore = useTranscriptionStore();

  const { transcriptionStatus } = storeToRefs(transcriptionStore);
  const { type, commentsEnabled } = storeToRefs(store);
  const { isCurrentItemOwner } = useUser();

  const analyticsTabEnabled = computed(() => isCurrentItemOwner.value);

  const transcriptionTabEnabled = computed(() => {
    return (
      type.value === ItemType.VIDEO &&
      (isCurrentItemOwner.value ||
        (transcriptionStatus.value && [ItemTranscriptionStatus.DONE, ItemTranscriptionStatus.EDITED].includes(transcriptionStatus.value)))
    );
  });

  const isTabListVisible = computed(() => {
    return commentsEnabled.value || transcriptionTabEnabled.value || analyticsTabEnabled.value;
  });

  const updateAvailableTabs = () => {
    availableTabs.value = [];

    if (commentsEnabled.value) {
      availableTabs.value.push(SubheaderTabs.DISCUSSION);
    }

    if (transcriptionTabEnabled.value) {
      availableTabs.value.push(SubheaderTabs.TRANSCRIPTIONS);
    }

    if (analyticsTabEnabled.value) {
      availableTabs.value.push(SubheaderTabs.ANALYTICS);
    }
  };

  const selectTab = (tab: SubheaderTabs, scrollTo?: boolean) => {
    selectedTab.value = tab;
    window.location.hash = tab;

    if (scrollTo) {
      const tabElement = document.querySelector(DEFAULT_TABS_ID);
      if (tabElement) {
        const { y } = tabElement.getBoundingClientRect();
        nextTick(() => {
          window.scrollTo({
            top: window.scrollY + y + SCROLL_OFFSET - window.innerHeight,
            behavior: 'smooth',
          });
        });
      }
    }
  };

  watch(
    () => [analyticsTabEnabled.value, commentsEnabled.value, transcriptionTabEnabled.value, selectedTab.value].join(' '),
    () => {
      updateAvailableTabs();
      if (!availableTabs.value.includes(selectedTab.value) && availableTabs.value.length) {
        selectedTab.value = availableTabs.value[0];
      }
    }
  );

  onMounted(() => {
    updateAvailableTabs();

    selectedTab.value = availableTabs.value[0];
    const windowTab = window.location.hash;

    if (windowTab && availableTabs.value.includes(windowTab as SubheaderTabs)) {
      selectTab(windowTab as SubheaderTabs, true);
    }
  });

  const onTabChange = (tab: SubheaderTabs) => {
    selectedTab.value = tab;
  };

  return {
    onTabChange,
    selectTab,
    selectedTab,
    commentsEnabled,
    transcriptionTabEnabled,
    analyticsTabEnabled,
    isTabListVisible,
  };
};
